<script setup lang="ts">
  import { useModal } from '@/composition/use-modal';
  import ModalsStockItems from '../stock/ModalsStockItems.vue';
  import { IStockItem } from '@/interfaces/IStockItem';
  import { IItem } from '@/interfaces/IItem';
  import { computed, onMounted, reactive, ref } from 'vue';
  import { IStockRecipe } from '@/interfaces/IStockRecipe';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import ControlNumberInput from '@/components/control/ControlNumberInput.vue';
  import { CustomError } from '@/exceptions/custom.exception';
  import { StockRecipesService } from '@/api/services/stock-recipes.service';
  import ModalsItemStockSection from './ModalsItemStockSection.vue';
  import { IOptionItem } from '@/interfaces/IOptionItem';

  const modals = useModal();
  const restaurantStore = useRestaurantStore();
  const ui = useUi();

  const props = defineProps<{
    item: IItem;
    value?: IStockRecipe[];
  }>();

  const emit = defineEmits(['input']);

  const stockRecipes = computed({
    get: () => props.value ?? [],
    set: (value: IStockRecipe[]) => {
      emit('input', value);
    },
  });

  const baseItemRecipes = computed(() => {
    if (!stockRecipes.value) return undefined;

    return stockRecipes.value.filter(
      (recipe) => recipe.itemId && !recipe.optionItemId
    );
  });

  const itemVariantsRecipes = computed(() => {
    if (!stockRecipes.value) return undefined;

    return stockRecipes.value.filter(
      (recipe) => recipe.itemId && !!recipe.optionItemId
    );
  });

  async function loadStockRecipes() {
    try {
      const itemRecipes = await StockRecipesService.findByItemId(
        restaurantStore.restaurant.id,
        restaurantStore.selectedBranch.id,
        props.item.id
      );
      stockRecipes.value = itemRecipes;
    } catch (e) {
      ui.serverError(e, 'Error al cargar los componentes de stock');
    }
  }

  onMounted(() => {
    loadStockRecipes();
  });

  const itemVariants = computed(() => {
    return props.item.priceVariants;
  });

  const itemOptions = computed(() => {
    const links = props.item.itemOptions ?? [];

    return links.filter((iO) => {
      if (!iO || iO.deleted) return false;
      const o = iO.option;
      if (!o || o.main) return false;

      // Mirror backend effective-active semantics:
      // oldItemOption.active = itemOption.active ?? option.active ?? true;
      return (iO.active ?? o.active ?? true) === true;
    });
  });

  function openStockItemsModal({ optionItem }: { optionItem?: IOptionItem }) {
    modals.show(
      ModalsStockItems,
      {},
      {
        name: 'stock-items',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: true,
      },
      {
        chooseItem: (stockItem: IStockItem) => {
          modals.hide('stock-items');
          addStockItem(stockItem, optionItem);
        },
      }
    );
  }

  function addStockItem(stockItem: IStockItem, optionItem?: IOptionItem) {
    if (
      stockRecipes.value?.find(
        (item) =>
          item.stockItemId === stockItem.id &&
          item.optionItemId === optionItem?.id
      )
    ) {
      ui.showToast(AlertType.Error, 'Este componente de stock ya fue agregado');
      return;
    }

    stockRecipes.value = [
      ...stockRecipes.value,
      {
        id: -Math.floor(Math.random() * 10000000),
        itemId: props.item.id,
        restaurantId: restaurantStore.restaurant.id,
        branchId: restaurantStore.selectedBranch.id,
        stockItemId: stockItem.id,
        stockItem,
        quantity: 1,
        optionId: optionItem?.optionId,
        optionItemId: optionItem?.id,
      },
    ];
  }

  const editionData = reactive({
    editedIds: new Set<number>(),
    deletedIds: new Set<number>(),
  });

  function updateStockRecipeQuantity({
    stockRecipe,
    quantity,
  }: {
    stockRecipe: IStockRecipe;
    quantity: number;
  }) {
    stockRecipe.quantity = quantity;

    if (stockRecipe.id > 0) {
      editionData.editedIds.add(stockRecipe.id);
    }
  }

  function deleteStockRecipe(stockRecipe: IStockRecipe) {
    stockRecipes.value = stockRecipes.value.filter(
      (item) => item.id !== stockRecipe.id
    );

    if (stockRecipe.id > 0) {
      editionData.editedIds.delete(stockRecipe.id);
      editionData.deletedIds.add(stockRecipe.id);
    }
  }

  async function handleSave(item: IItem) {
    const saveData = {
      recipesToCreate: [],
      recipesToUpdate: [],
    };

    for (const stockRecipe of stockRecipes.value) {
      if (editionData.editedIds.has(stockRecipe.id)) {
        saveData.recipesToUpdate.push(stockRecipe);
      } else if (stockRecipe.id < 0) {
        saveData.recipesToCreate.push(stockRecipe);
      }
    }

    try {
      await StockRecipesService.massUpdate({
        restaurantId: restaurantStore.restaurant.id,
        branchId: restaurantStore.selectedBranch.id,
        toAdd: saveData.recipesToCreate,
        toUpdate: saveData.recipesToUpdate,
        toDelete: Array.from(editionData.deletedIds),
      });
    } catch (e) {
      ui.serverError(e, 'Error al guardar los componentes de stock');
      throw new CustomError('Error al guardar los componentes de stock');
    }
  }

  defineExpose({
    handleSave,
  });
</script>

<template>
  <div class="modal-item-stock">
    <template v-if="!item.id">
      <v-alert type="info" class="cannot-edit-message" dense>
        Guarde el item y vuelva a abrir esta pantalla de edición para poder
        agregar componentes de stock.
      </v-alert>
    </template>
    <template v-else>
      <ModalsItemStockSection
        title="Componentes / ingredientes base"
        :stockRecipes="baseItemRecipes"
        @updateQuantity="updateStockRecipeQuantity"
        @showAdd="openStockItemsModal"
        @delete="deleteStockRecipe"
      />
      <template v-if="itemVariants">
        <div class="divider"></div>
        <span class="section-title">Componentes / ingredientes variantes</span>
        <span class="section-subtitle">
          Este item tiene precios múltiples, por lo que a los ingredientes base
          se les sumarán los ingredientes del precio seleccionado.
        </span>
        <ModalsItemStockSection
          v-for="optionItem of itemVariants.optionItems"
          :key="optionItem.id"
          :title="`Componentes / ingredientes variantes de opción '${optionItem.title}'`"
          :optionItem="optionItem"
          :stockRecipes="
            itemVariantsRecipes.filter(
              (recipe) => recipe.optionItemId === optionItem.id
            )
          "
          @updateQuantity="updateStockRecipeQuantity"
          @delete="deleteStockRecipe"
          @showAdd="openStockItemsModal"
        />
      </template>
      <template v-if="itemOptions.length">
        <div class="divider"></div>
        <span class="section-title">Opciones</span>
        <span class="section-subtitle">
          Este item tiene opciones, por lo que a los ingredientes base se les
          sumarán los ingredientes de las opciones seleccionadas.
          <br />
          Para editar los ingredientes de las opciones, vaya a Menu - Opciones y
          abra la pantalla de edición de la opción.
        </span>
      </template>
    </template>
  </div>
</template>

<style scoped lang="scss">
  .modal-item-stock {
    @apply tw-flex tw-flex-col tw-space-y-4 tw-items-start tw-pt-4 tw-pb-2;

    .divider {
      @apply tw-w-full tw-border-b tw-border-gray-300 tw-border-dashed tw-mx-4;
    }

    .cannot-edit-message {
      @apply tw-w-full;
    }

    .section-title {
      @apply tw-px-4 tw-font-medium;
    }

    .section-subtitle {
      @apply tw-px-4 tw-text-sm tw-text-gray-500;
    }
  }
</style>
